import {
  BRAND_VOICE_REGEN_CACHE_KEY,
  useGetAvailableRewardsQuery,
  useGetCannedResponsesQuery,
  useGetRecoveryConfigQuery,
  useGetSandboxPromptQuery,
  useRegenerateCannedResponsesMutation,
} from 'api/recoveryAi'
import type {
  AvailableReward,
  MockOrderScenario,
  RecoveryConfig,
  RecoveryConversationContextInput,
  SandboxPrompt,
} from 'api/recoveryAi.types'
import { useCallback, useMemo } from 'react'
import { useMakeGoodCuration } from '../components/useMakeGoodCuration'
import { getFreeChatOrder } from './freeChatOrder'

type UseSimulatorDataOptions = {
  /**
   * Relax the configure-first gate so the engine renders BEFORE
   * `onboarding_completed` and approved rewards exist. Used by the
   * wizard-embedded shell, where the merchant tests the agent before
   * activation. Defaults to false, preserving the in-tab sandbox's
   * configure-first guard.
   */
  preActivation?: boolean
  /** The mock order scenario currently driving the conversation, if any. */
  scenario?: MockOrderScenario | null
}

type UseSimulatorDataResult = {
  config: RecoveryConfig | undefined
  approvedRewards: AvailableReward[]
  conversationContext: RecoveryConversationContextInput | null
  sandboxPrompt: SandboxPrompt | undefined
  /** The server-resolved greeting; undefined until it loads. */
  cannedGreeting: string | undefined
  /**
   * A canned-response rebuild triggered by a brand-voice save is in flight, so
   * `cannedGreeting` is still the PREVIOUS voice for the next few seconds.
   *
   * Read from the SHARED mutation cache key, not a local trigger: the rebuild is
   * dispatched by api/recoveryAi's updateRecoveryConfig onQueryStarted, which has
   * no component tuple to expose pending state. This hook only observes it — it
   * must never fire it (that would rebuild the copy, at one LLM call, on every
   * mount of the Testing step).
   *
   * Does NOT gate `isLoading`/`ready`: a rebuild is a refinement of copy that
   * already has a working fallback, so spinner-ing the whole simulator would
   * claim the sandbox is unusable when it is not.
   */
  isGreetingAdapting: boolean
  isLoading: boolean
  /**
   * The config or rewards GET failed. Distinct from `!ready`: a network error
   * is NOT "unconfigured". Consumers must surface this as a recoverable error
   * region instead of the configure-first gate (or, in the wizard, an infinite
   * spinner). Fixing it here means both shells get it from one seam.
   */
  error: boolean
  /**
   * AI-185: re-run the failed config/rewards GET(s) in place (no remount) so an
   * error state offers recovery without leaving the Testing gate. Stable ref.
   */
  retry: () => void
  ready: boolean
  assistantName: string | null | undefined
}

/**
 * Owns the config/rewards/prompt queries and the derived conversation data for
 * the recovery simulator, extracted from the original in-tab sandbox shell
 * (AI-95). The `preActivation` flag is the single switch that relaxes the
 * configure-first gate so the wizard can embed the simulator before the
 * merchant activates.
 */
export const useSimulatorData = ({
  preActivation = false,
  scenario = null,
}: UseSimulatorDataOptions = {}): UseSimulatorDataResult => {
  const {
    data: config,
    isLoading: configLoading,
    isError: configError,
    refetch: refetchConfig,
  } = useGetRecoveryConfigQuery()
  const {
    data: availableRewards,
    isLoading: rewardsLoading,
    isError: rewardsError,
    refetch: refetchRewards,
  } = useGetAvailableRewardsQuery()
  const { data: sandboxPrompt } = useGetSandboxPromptQuery()
  // Pre-activation-only: the wizard's simulator runs before the merchant has
  // enabled RecoveryAI, so no make-good offer is materialized yet
  // (available_rewards' `.offerable` scope requires granted_program_id) and
  // `availableRewards` below always comes back with zero make-good rows even
  // though the curation ladder already shows N grantable rewards. Skipped
  // entirely outside preActivation — the real (post-activation) sandbox must
  // keep reflecting the true offerable set, not the ladder.
  const { offers: curationOffers } = useMakeGoodCuration({
    skip: !preActivation,
  })
  // Fire-and-forget: unlike config/rewards, a loading or failed canned-responses
  // GET must NOT gate the simulator (`error`/`isLoading` below are deliberately
  // built from configError/rewardsError only) — the greeting has a bundled
  // fallback, the rest of the sandbox does not.
  const { data: cannedResponses } = useGetCannedResponsesQuery()
  const cannedGreeting =
    cannedResponses?.find(r => r.responseType === 'greeting')?.body?.trim() ||
    undefined

  // Observe-only subscription to the rebuild a brand-voice save kicks off. The
  // trigger is deliberately discarded: this hook reports the state, the api layer
  // owns the dispatch (see BRAND_VOICE_REGEN_CACHE_KEY).
  const [, { isLoading: isGreetingAdapting }] =
    useRegenerateCannedResponsesMutation({
      fixedCacheKey: BRAND_VOICE_REGEN_CACHE_KEY,
    })

  const isLoading = configLoading || rewardsLoading
  // A failed config/rewards GET must read as a recoverable error, NOT as the
  // configure-first gate (the merchant IS configured; the network failed). The
  // sandbox prompt is optional, so its failure does not gate the simulator.
  const error = configError || rewardsError

  // AI-185: re-run only the GET(s) that actually failed. refetch re-triggers the
  // existing query subscription, so isError stays true until it resolves and the
  // shells swap error → spinner → engine with NO remount. Stable ref so it never
  // churns the SimulatorStatus button across renders.
  const retry = useCallback(() => {
    if (configError) refetchConfig()
    if (rewardsError) refetchRewards()
  }, [configError, rewardsError, refetchConfig, refetchRewards])

  // Synthesizes the SAME wire shape available_rewards.rb's make_good_offer_rows
  // emits for a materialized make-good offer post-activation
  // (id: `make_good:<id>`, cost_cents: half of up_to_cents — the assumed-COGS
  // convention Recovery::MakeGoodOffer#assumed_cost_cents shares with
  // BuildConsumerContext — type: 'manual_percent', image_url/points always
  // null, ai_generated: true), sourced from the curation ladder instead of a
  // real offerable row. Declined tiers are excluded — same set
  // useMakeGoodCuration's visibleCount counts, and the same set the settings
  // panel's ladder renders.
  const synthesizedMakeGoodRewards = useMemo<AvailableReward[]>(
    () =>
      curationOffers
        .filter(offer => !offer.declined)
        .map(offer => ({
          id: `make_good:${offer.id}`,
          source: 'make_good',
          label: offer.name,
          cost_cents: Math.round(offer.upToCents / 2),
          type: 'manual_percent',
          active: true,
          image_url: null,
          value_cents: offer.upToCents,
          points: null,
          ai_generated: true,
        })),
    [curationOffers]
  )

  // Filter available rewards to those approved in config AND still active,
  // PLUS every make-good offer row regardless of approved_reward_ids — mirrors
  // BuildConsumerContext#serialize_approved_rewards (the real resolve path),
  // which serves approved templates + ALL offerable make-good offers rather
  // than filtering make-good by the merchant's template approval list. The
  // active check matters when a campaign behind an approved reward is later
  // deactivated via the campaigns admin — the merchant hasn't un-approved it
  // here, but the AI should not offer a dead reward.
  const approvedRewards = useMemo(() => {
    if (!availableRewards || !config?.approved_reward_ids) return []
    const rewards = availableRewards.filter(
      r =>
        r.source === 'make_good' ||
        (r.active && config.approved_reward_ids.includes(r.id))
    )
    // Belt + suspenders: a real materialized make-good row (shouldn't exist
    // pre-activation, but if one does) always wins over the synthesized ladder
    // entries — never both, so the count the wizard shows can't double up.
    if (!preActivation || rewards.some(r => r.source === 'make_good')) {
      return rewards
    }
    return [...rewards, ...synthesizedMakeGoodRewards]
  }, [
    availableRewards,
    config?.approved_reward_ids,
    preActivation,
    synthesizedMakeGoodRewards,
  ])

  // Build conversation context once config has loaded — regardless of scenario.
  // The free-chat Agent-Tuning sandbox has no scenario, but it MUST still send
  // the merchant's saved brand voice (BUG-214); gating on `!scenario` dropped it.
  // The `!config` wait still stands: without it the hook would lock in defaults
  // (empty brand voice, balanced posture) before the real config arrives,
  // silently overriding it for the whole chat.
  const conversationContext =
    useMemo<RecoveryConversationContextInput | null>(() => {
      if (!config) return null
      return {
        brand_voice_text: config.brand_voice_text || '',
        philosophy_posture: config.philosophy_posture || 'balanced',
        // AI-1147: thanx-ai's Talker (giver_interactor) reads ONLY
        // reward["label"] off approved_rewards — `label` on the wire is the
        // merchant-internal template name (CMS "only visible to you"), while
        // `customer_label` is the guest-facing text. Swap it in here, in the
        // BRAIN-BOUND payload only, so the brain never quotes the internal
        // name back to the customer; `approvedRewards` itself is left alone
        // since the sandbox's own reward-preview cards (SandboxRewardCard's
        // catalog variant) still need the real label. `.trim() || r.label`
        // (mirrors `cannedGreeting` above) falls back to `label` when
        // `customer_label` is absent, blank, or whitespace-only — `string |
        // null` allows nexus to ship `''`, and an empty reward name must not
        // reach the brain.
        approved_rewards: approvedRewards.map(r => ({
          ...r,
          label: r.customer_label?.trim() || r.label,
        })),
        // `?? 10000` not `|| 10000`: a legitimate $0 daily budget (agent grants
        // nothing) must reach the brain as 0, not be coerced to $100.
        daily_budget_cents: config.default_daily_budget_cents ?? 10000,
        // `.order`, NOT the scenario wrapper. The brain reads the affected
        // items as `JSON.parse(order_context_json)["items"]`
        // (thanx-ai giver_interactor.rb:167). `items` lives on MockOrder, not
        // on MockOrderScenario, so sending the wrapper made every priced-item
        // lookup come back empty and escalated the turn as `no_priced_items`
        // before the engine ever ran.
        //   context: free chat falls back to a priced stand-in rather than null
        //   for the same reason — an order-less turn zeroes the Bouncer's anchor
        //   and escalates terminally, so a resolution was unreachable on the
        //   Agent Tuning tab and the wizard's "Test your own" phase. See
        //   freeChatOrder.ts, whose fields reach the Talker's system prompt.
        //   context: getFreeChatOrder(), not buildFreeChatOrder() — the same
        //   day-cached order SimulatorEngine falls back to for
        //   OrderDetailsCard, so the card can never show a DIFFERENT free-chat
        //   order than the one actually sent here.
        order_context: scenario?.order ?? getFreeChatOrder(),
        sandbox_prompt_text: sandboxPrompt?.prompt_text ?? '',
        // Static per-scenario fixture (personaScenarios.ts) — free chat /
        // Agent Tuning has no scenario, so it sends null (renders every
        // dossier signal "Unknown") rather than a fabricated placeholder.
        dossier: scenario?.dossier ?? null,
      }
    }, [config, approvedRewards, scenario, sandboxPrompt?.prompt_text])

  // `preActivation` relaxes the configure-first gate: once data has loaded the
  // engine renders regardless of onboarding/reward state. Otherwise preserve the
  // original gate — onboarding complete AND at least one ACTIVE approved reward.
  // Either way `config` must be present: a failed config GET leaves config
  // undefined with isLoading false, and we must NOT render a context-less engine
  // (conversationContext would be null) — gate on Boolean(config) so the caller
  // keeps showing the spinner instead of a silently-unconfigured simulator.
  const isConfigured = Boolean(
    config?.onboarding_completed && approvedRewards.length > 0
  )
  const ready = preActivation
    ? !isLoading && Boolean(config)
    : !isLoading && isConfigured

  return {
    config,
    approvedRewards,
    conversationContext,
    sandboxPrompt,
    cannedGreeting,
    isGreetingAdapting,
    isLoading,
    error,
    retry,
    ready,
    assistantName: config?.assistant_name,
  }
}
